// frontend/src/pages/KnShipments.jsx
//
// EK-D17 / UI2 Teil 10 G12a — die Liste der gespiegelten Kuehne+Nagel-Sendungen.
// NUR LESEND.
//
// Sie beantwortet beim Hinsehen drei Fragen, und das ist ihre Gliederung:
//   1. Wo ist die Sendung?   Stufe, Herkunft → Ziel
//   2. Wann kommt sie?       Ankunft Hafen · Zustellung — ZWEI Spalten
//   3. Stimmt etwas nicht?   der Leistungshinweis, der Stand des Spiegels
//
// 🔴 „Ankunft Hafen" und „Zustellung" sind ZWEI Spalten und werden nie
//    zusammengefasst. Die Zustellung (`eta_at`/`arrived_at`) ist heute bei
//    allen drei Sendungen leer, weil in den bestellten Leistungen kein `del`
//    steht und K&N kein Zustelldatum liefert; die Hafenankunft
//    steht in `raw` und ist das einzige Datum, das K&N liefert. Wer beide in
//    eine Spalte legt, macht aus einem Hafentermin eine Lieferzusage.
//
// 🔴 Kein Eingabefeld, kein Speichern. Der Stand ist gespiegelt.
//
// ── G12a (2026-10-21): was diese Fassung hinzufuegt ────────────────────────
// Breite `full` statt Deckel `reading`, Zonenmarken (P1–P3, P8), die
// Listensuche ueber `useListQuery` (EIN Schreiber, `?q=` in der Adresse),
// einen Zaehler „n of m" (`gesamt` vom Server), den Seitenwechsel ueber die
// geladene Menge (50 je Seite), `TableViewport` statt des rohen
// `overflowX`-Kastens, `MobileRecordCard` statt der handgerollten Karte und
// drei Leerzustaende ueber `EmptyState` (`no-data`, `no-match`, `error`).
//
// 🔴 **Die Suche ist eine LISTENSUCHE, keine Parteisuche.** Sie greift
//    serverseitig ueber fuenf Felder — Tracking, Buchung, Abgangs- und
//    Zielhafen sowie die K&N-Kennung (`source_shipment_id`, R16 W4,
//    Nachbesserung 1) —, nicht ueber Absender, Empfaenger oder Ware.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Link, Skeleton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import ViewHeader from '../components/ViewHeader'
import ListToolbar from '../components/common/ListToolbar'
import TableFooterSimple from '../components/common/TableFooterSimple'
import TableViewport from '../components/common/TableViewport'
import MobileRecordCard, { KARTE_TITEL_BASIS_SX } from '../components/common/MobileRecordCard'
import EmptyState from '../components/common/EmptyState'
import Pill from '../components/common/Pill'
import useContainerWidth from '../components/common/useContainerWidth'
import useColumnPriority from '../components/common/useColumnPriority'
import useListQuery from '../components/common/listQuery/useListQuery'
import LeistungsHinweis from '../components/kn/LeistungsHinweis'
import { api } from '../api/client'
import {
  COLUMN_MAX, CONTENT_WIDTH, FS, SP,
} from '../theme/tokens'
import { formatBerlinDate, formatBerlinDateTime } from '../utils/time'
import { STRICH, ortText, stufeText } from '../utils/knAnzeige'

/**
 * F9 (UI-Durchgang 2, 2026-10-07): die acht Kopfzellen brechen nicht um.
 * „PORT ARRIVAL" und „FINAL DELIVERY" standen zweizeilig uebereinander; die
 * Hausform ist `whiteSpace: 'nowrap'` (`pages/MarketLab.jsx`).
 */
const KOPF_SX = { whiteSpace: 'nowrap' }

/** 50 je Seite ist die Hausgroesse (§9.6), keine Nutzerwahl. */
const ROWS_PER_PAGE = 50

/**
 * Die Spalten der Tabelle — Deskriptoren fuer `useColumnPriority`.
 *
 * 🔴 **`kern` traegt die drei Fragen der Seite.** „Notice" ist die dritte
 * Frage („stimmt etwas nicht") und bleibt deshalb bei JEDER Breite; „Mode"
 * und „As of" sind Zusatzangaben und fallen unter ~1544 px weg. Wer die
 * tragende Spalte auf `lg` legt, verliert sie auf dem Schreibtisch.
 */
const SPALTEN = [
  { key: 'tracking', priority: 'kern', kind: 'identity' },
  { key: 'stage', priority: 'kern', kind: 'chip' },
  { key: 'mode', priority: 'lg', kind: 'text' },
  { key: 'route', priority: 'kern', kind: 'text' },
  { key: 'hafen', priority: 'kern', kind: 'date' },
  { key: 'zustellung', priority: 'kern', kind: 'date' },
  { key: 'stand', priority: 'lg', kind: 'date' },
  // 🔴 R16 W3 (Nachbesserung 1, 2026-10-22): `text`, nicht `chip`. Die Zelle
  //    traegt den `knapp`-SATZ des Leistungshinweises (~78 Zeichen), kein
  //    Etikett; `COLUMN_MAX.chip` ist mit „Paid, Order placed" auf Etikettbreite
  //    kalibriert (`tokens.js`). Bei 1440 brach der Satz dadurch auf vier Zeilen
  //    und bestimmte die Zeilenhoehe der ganzen Liste. `text` ist die Art, die
  //    fuer einen Satz da ist.
  { key: 'hinweis', priority: 'kern', kind: 'text' },
]

/**
 * Ein Termin mit seiner Verbindlichkeit — Plan und Ist sind SICHTBAR verschieden.
 *
 * 🔴 **R16 B1 (Nachbesserung 1, 2026-10-22): Datum und Pille stehen UNTEREINANDER.**
 * Bis hierher standen sie in EINER Zeile (`direction="row"`). Die beiden
 * Spalten sind als `kind: 'date'` deklariert, und `COLUMN_MAX.date` ist auf ein
 * NACKTES Datum kalibriert („31.07.2026", 96–160 px); der Inhalt der Zelle
 * brauchte gemessen ~147 px. Unterhalb eines Behaelters von ~1290 px wurde die
 * Zelle schmaler als ihr Inhalt, und `tableLayout: 'fixed'` klemmt nicht ab —
 * bei 1280 px deckte der „Planned"-Kasten 11 px des Zustellungswerts in der
 * Nachbarspalte zu (pixelgemessen am Foto `g12a1_zweig/1280_grund.png`).
 *
 * Die Spaltenbreite bleibt damit beim Datum, und die Pille kann nirgends
 * hineinlaufen. Den Preis (etwas Zeilenhoehe) traegt ohnehin die NOTICE-Spalte.
 *
 * 🔴 **R15 W5 (dieselbe Runde): die Pille ist der geteilte `Pill`, nicht MUI
 * `Chip`.** Rule 22 §Component rules: „Pick one; use it." Der `Chip` war aus
 * der alten Fassung uebernommen, die `Pill` steht daneben schon auf der Karte.
 */
function TerminZelle({ zeitpunkt, art }) {
  if (!zeitpunkt) {
    return <Typography component="span" sx={{ color: 'text.secondary' }}>{STRICH}</Typography>
  }
  return (
    <Stack gap={SP.chip} component="span" alignItems="flex-start">
      <Typography component="span" sx={{ fontSize: FS.sm }}>
        {formatBerlinDate(zeitpunkt)}
      </Typography>
      {art && (
        <Pill
          // 🔴 Die Marke traegt die Probe bei 1280 (`sendungen.spec.ts`): sie
          //    misst, ob der Kasten in SEINER Zelle bleibt. Ohne sie muesste die
          //    Probe die Pille ueber ihren Text suchen — und ein Text, den die
          //    Nachbarspalte auch traegt, macht die Messung mehrdeutig.
          data-termin-pille=""
          tone={art === 'erreicht' ? 'ok' : 'plain'}
        >
          {art === 'erreicht' ? 'Actual' : 'Planned'}
        </Pill>
      )}
    </Stack>
  )
}

/** Die Zustellung einer Zeile — `arrived_at` schlaegt `eta_at`, nie beide. */
function zustellung(s) {
  return {
    zeitpunkt: s.arrived_at || s.eta_at,
    art: s.arrived_at ? 'erreicht' : (s.eta_at ? 'geplant' : null),
  }
}

/** Die Route als EIN Text — zweimal dieselbe Ableitung fuer Tabelle und Karte. */
function routeText(s) {
  return `${ortText(s.origin_code, s.origin_name)} → ${ortText(s.destination_code, s.destination_name)}`
}

export default function KnShipments() {
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  // ── Der Listen-Zustand liegt in der ADRESSE (§3.1) ───────────────────────
  // Genau zwei Parameter: `q` (Freitext) und `page` (ab 1). Kein `tab`, kein
  // `sort`, kein `pageSize` — es gibt nichts zu waehlen.
  const query = useListQuery({
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
    pageSize: ROWS_PER_PAGE,
  })
  const { state, set, clearAll, chips } = query
  const q = state.q || ''

  // ── Der Abruf ────────────────────────────────────────────────────────────
  const [sendungen, setSendungen] = useState([])
  const [gesamt, setGesamt] = useState(null)
  const [abgeschnitten, setAbgeschnitten] = useState(false)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  // 🔴 `jeGeladen` heisst „es liegen Daten vor", nicht „es kam eine Antwort".
  // Es traegt zwei Dinge: die Sperre des ersten Aufbaus (N8.5) und die
  // Entscheidung, ob ein Nachladen das Skelett zeigt.
  const [jeGeladen, setJeGeladen] = useState(false)
  // 🔴 R15 W4 (Nachbesserung 2, 2026-10-22): derselbe Zustand als REF, nur fuer
  // den Effekt. Ein neuer Ladevorgang loescht den Text des VORVERSUCHS — ohne
  // das stuende „could not be refreshed" ueber einer Liste, die gerade
  // nachlaedt. Den Fehler des ERSTEN Aufbaus raeumt er NICHT weg: der ist der
  // Grund der Sperre (N8.5) und bleibt stehen, bis wirklich etwas geladen ist.
  // (Kein Dep: `jeGeladen` in der Abhaengigkeitsliste wuerde nach dem ersten
  // Erfolg einen zweiten Abruf ausloesen.)
  const jeGeladenRef = useRef(false)
  // Der Wiederhol-Zaehler ist der EINE Weg, denselben Abruf erneut zu fahren.
  const [versuch, setVersuch] = useState(0)

  useEffect(() => {
    const steuerung = new AbortController()
    let abgemeldet = false
    setLaedt(true)
    // Der Vorversuch ist ab hier nicht mehr der Stand — der Hinweis nennt sonst
    // einen Fehler, der gerade nicht mehr gilt (R15 W4).
    if (jeGeladenRef.current) setFehler(null)
    api.get('/api/kn/shipments', {
      params: { q: q || undefined },
      signal: steuerung.signal,
    })
      .then(({ data }) => {
        if (abgemeldet) return
        setSendungen(Array.isArray(data?.shipments) ? data.shipments : [])
        setGesamt(Number.isFinite(data?.gesamt) ? data.gesamt : null)
        setAbgeschnitten(Boolean(data?.abgeschnitten))
        setFehler(null)
        setJeGeladen(true)
        jeGeladenRef.current = true
      })
      // Kein stilles `.catch(() => {})` — ein Fehler, der die Anzeige
      // bestimmt, muss sichtbar sein. 🔴 Ein ABGEBROCHENER Lauf ist aber kein
      // Fehler (Hausform `KnShipmentSelect.jsx:133-137`): der naechste laeuft
      // schon, und ein Fehlertext flackerte bei jedem Tastendruck auf.
      .catch((e) => {
        if (abgemeldet || steuerung.signal.aborted) return
        setFehler(e?.message || 'Unknown error')
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true; steuerung.abort() }
  }, [q, versuch])

  const wiederholen = () => setVersuch((v) => v + 1)

  // 🔴 Die Sperre des ERSTEN Aufbaus: nur solange noch nie etwas geladen wurde.
  // Ein spaeter gescheitertes Auffrischen laesst die Zeilen stehen (N8.5).
  const sperre = !jeGeladen && fehler != null

  // ── Seitenwechsel: clientseitig ueber die geladene Menge ─────────────────
  // Der Server kappt bei 500; mehr als das liegt hier nie. `page` kommt
  // 1-basiert aus dem Hook, `TableFooterSimple` rechnet 0-basiert — die
  // Umrechnung steht an GENAU EINER Stelle, und die Klemmung SCHREIBT die URL
  // nicht um: die Ansicht zeigt die letzte Seite (N10.4), sie behauptet keine.
  const seiten = Math.max(1, Math.ceil(sendungen.length / ROWS_PER_PAGE))
  const seite = Math.min(Math.max(1, Number(state.page) || 1), seiten)
  const sichtbareZeilen = useMemo(
    () => sendungen.slice((seite - 1) * ROWS_PER_PAGE, seite * ROWS_PER_PAGE),
    [sendungen, seite],
  )

  // ── Breite und Spaltenbudget ─────────────────────────────────────────────
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  // Der Rueckfall VOR der ersten Messung ist das Muster der Seite — nie 0.
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.full ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  const sichtbarSpalte = useMemo(
    () => new Set(sichtbareSpalten.map((s) => s.key)),
    [sichtbareSpalten],
  )
  const spaltenBudget = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0),
    0,
  )
  // Die UNTERGRENZE: darunter rollt der `TableViewport` waagerecht, statt die
  // Kernspalten zu quetschen. Erst dadurch hat der Randhinweis (`edgeHint`)
  // einen Gegenstand — ohne sie waere die Tabelle immer genau so breit wie ihr
  // Kasten, und der Hinweis koennte nie erscheinen.
  const spaltenMinSumme = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.min ?? 0),
    0,
  )
  // Der Deckel der Tabelle = min(Behaelter, Budget) — der Aufrufer wendet ihn
  // an, `TableViewport` meldet ihn nur (G0b.4).
  const tabellenMax = Math.min(messBasis, spaltenBudget)
  // 🔴 Die Spaltenbreiten kommen aus `COLUMN_MAX[kind].max`, anteilig am
  // Budget — `tableLayout: 'fixed'` haelt die Kopfzellen-x damit UNABHAENGIG
  // vom Zeileninhalt. Genau das braucht P33: es vergleicht die Kopfpositionen
  // zwischen `grund` und dem Leerzustand `?q=zzzz`, und ohne feste Breiten
  // verteilte der Browser die Spalten im leeren Zustand anders.
  const spaltenBreite = (key) => {
    if (spaltenBudget <= 0) return {}
    const kind = SPALTEN.find((s) => s.key === key)?.kind
    const max = COLUMN_MAX[kind]?.max ?? 0
    return { width: `${(max / spaltenBudget) * 100}%` }
  }

  // ── Die Leerzustaende ────────────────────────────────────────────────────
  // 🔴 An `!laedt` gebunden (W14): ohne die Bindung zeigte ein Deep-Link
  // `?q=…` beim ersten Aufbau kurz `no-match` — eine Aussage ueber eine Suche,
  // die noch nicht gelaufen ist. `null` heisst „keine Aussage".
  //
  // 🔴 **R15 B3 / R16 W2 (Nachbesserung 1, 2026-10-22): `error` NUR bei leerer
  // Menge.** Vorher war jeder Fehler `'error'`, auch ein gescheitertes
  // NACHLADEN. Dann stand auf dem Schreibtisch eine Zeile „Shipments could not
  // be loaded" UNTER den alten Zeilen (eine Aussage ueber eine leere Liste, die
  // nicht leer ist) und auf dem Telefon ERSETZTE der Fehlerblock die geladenen
  // Karten. Plan §2.2a Nr. 10 / N8.5 sagt: „ein spaeter gescheitertes
  // Auffrischen laesst die Zeilen stehen." Hausform ist `RedirectReviewInbox`
  // (`:365-369`), das den Grund ebenfalls nur bei leerer Ergebnismenge ableitet.
  const leerGrund = laedt
    ? null
    : (fehler && sendungen.length === 0) ? 'error'
      : sendungen.length > 0 ? null
        : (q ? 'no-match' : 'no-data')

  // Der eigene Zustand „gescheitert, aber Zeilen stehen" — er hat eine eigene
  // Flaeche (Hinweis UEBER der Liste), keinen Leerzustand.
  const nachladeFehler = fehler != null && jeGeladen && sendungen.length > 0

  // Der Kappungstext bekommt den Suchfall: bei aktivem `q` sind es Treffer,
  // nicht Sendungen, und der Weg heraus ist ein anderer.
  const kappungsText = q
    ? 'There are more matches than this list shows — narrow the search.'
    : 'There are more shipments than this list shows. The most recently mirrored ones are displayed.'

  return (
    <PageContainer width="full">
      {/* 🔴 Zone 1 (`liste-kopf`) — P2/P3 lesen sie. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="K&N Shipments"
          subtitle="The mirrored state of shipments at Kühne+Nagel — read only"
        />
      </Box>

      {/* 🔴 N8.5: der Grund der Sperre steht als SICHTBARER Text DIREKT ueber
          der Leiste — mit der Haus-Marke `erster-aufbau-gesperrt`. Ohne sie
          findet die P9Sperre-Klausel nichts, und „gesperrt" waere unbelegt. */}
      {sperre && (
        <Alert severity="warning" sx={{ mb: SP.card }} data-testid="erster-aufbau-gesperrt">
          Search and filters are locked until the list loads. Use Try again below.
        </Alert>
      )}

      {/* 🔴 R15 B3 / R16 W2 (Nachbesserung 1, 2026-10-22): ein gescheitertes
          NACHLADEN hat eine eigene Flaeche UEBER der Liste — nicht den
          Leerzustand. Hausform `ScraperList.jsx:2139-2147`: `Alert` mit
          `role="status"` (nicht `alert`: der Hinweis ist eine Zustandsnotiz,
          keine Unterbrechung — und die Sperrmeldung darueber traegt dieselbe
          Rolle). Der Weg heraus steht als Knopf DANEBEN, damit „Try again"
          nicht ins Leere zeigt.

          🔴 R15 W1 (Nachbesserung 2, 2026-10-22): der Hinweis steht VOR
          `<Panel>`, nicht darin. P8 zaehlt `role="status"` INNERHALB von
          `liste-panel` und verlangt GENAU EINEN (`helfer.ts:1005`); als zweite
          Region im Panel machte er den Zaehler im Zustand „Nachladen
          gescheitert" doppelt — latent, weil P8 bisher nur im Normalzustand
          lief. Ueber der Liste bleibt er so oder so. */}
      {nachladeFehler && (
        <Alert
          severity="warning"
          role="status"
          data-testid="nachladen-gescheitert"
          sx={{ mb: SP.card }}
          action={(
            <Button color="inherit" size="small" onClick={wiederholen}>
              Try again
            </Button>
          )}
        >
          The list could not be refreshed: {fehler}. The rows below are the
          last successful load.
        </Alert>
      )}

      {/* 🔴 Zone 5 (`liste-panel`) umschliesst Z1–Z3, die Tabelle und die
          Fusszeile — und NUR sie: P8 zaehlt `role="status"` innerhalb dieser
          Zone, und der Kopf liegt ausserhalb. Aus demselben Grund liegen BEIDE
          Zustaende (Sperre, gescheitertes Nachladen) VOR dem Panel: sie sind
          Hinweise ueber der Liste, keine Regionen in ihr. */}
      <Panel pad="none" data-testid="liste-panel">
        <Box sx={{ px: SP.card, pt: SP.card }}>
          <ListToolbar
            shortcut="/"
            search={{
              value: q,
              // Puffermodus (`onCommit`): 300 ms Ruhe oder Enter uebergeben den
              // Begriff — er geht SERVERSEITIG als `q` in die Adresse.
              onCommit: (v) => set('q', v),
              // 🔴 Ein Text in BEIDEN Breiten (N7.4). Der zugaengliche Name
              // beginnt mit „Search" (P5); ohne Ellipse, weil die Ellipse kein
              // Beschriftungstext ist. `inputRole` gibt dem Feld die Rolle
              // `searchbox` — P5/P34 suchen genau die.
              // 🔴 R16 W4 (Nachbesserung 1, 2026-10-22): die K&N-Kennung ist
              //    jetzt durchsuchbar (`source_shipment_id` in `ansicht.py`) —
              //    der Text nennt sie deshalb. Vorher zeigte die Karte eine
              //    Nummer, die in dasselbe Feld getippt „No shipment matches"
              //    ergab.
              placeholder: 'Search tracking, booking, route or K&N ID…',
              label: 'Search tracking, booking, route or K&N ID',
              inputRole: 'searchbox',
              disabled: sperre,
              // N2.9/N7.3: die Suche fuellt den Rest von Z1.
              sx: { flex: 1, minWidth: 0 },
            }}
            // 🔴 GENAU EIN Zaehler, „n of m shipments". `m` kommt vom Server
            // (`gesamt`, ohne Suche und ohne Kappung) — ein Zaehler ueber die
            // geladene Seite muesste „n von n" behaupten. Bei Kappung steht
            // „500+ shipments", nie eine geratene Zahl.
            // 🔴 **R16 N1 (Nachbesserung 1, 2026-10-22): die Region steht ab dem
            // ERSTEN PAINT.** Vorher wurde `count` erst ab `jeGeladen` gereicht —
            // dann entstand die Live-Region zusammen mit ihrem Text, und genau
            // den Fall will `ResultCount` ausschliessen („The region exists from
            // the first paint", Docstring). `n`/`m` sind `null`, solange nichts
            // geladen ist: das ist die dokumentierte „unbekannt"-Lage und rendert
            // eine LEERE Region, nie eine geratene 0.
            //
            // 🔴 Ein gescheitertes Nachladen laesst `n`/`m` auf dem letzten
            // ERFOLGREICH geladenen Stand stehen. Das ist die Wahrheit ueber die
            // Zeilen, die auf dem Schirm stehen — der Zaehler rechnet nicht aus
            // dem gescheiterten Versuch. Der Hinweis ueber der Liste sagt
            // daneben, dass die Zeilen der letzte erfolgreiche Stand sind.
            count={{
              noun: 'shipments',
              n: jeGeladen ? sendungen.length : null,
              m: jeGeladen ? gesamt : null,
              unknownTotal: jeGeladen ? abgeschnitten : false,
              loading: laedt,
            }}
            // Z3 — die Chips und „Clear all".
            query={{ chips, clearAll }}
          />
        </Box>

        {/* 🔴 Der Sortiersatz traegt die Ordnung, weil es KEIN Bedienfeld gibt
            (P18 zulaesst „genau ein `aria-sort` ODER ein Satz"). */}
        <Box sx={{ px: SP.card }}>
          <Typography
            data-testid="sortiert-nach"
            sx={{ fontSize: FS.xs, color: 'text.secondary' }}
          >
            Sorted by next known date, undated last.
          </Typography>
        </Box>

        {!laedt && abgeschnitten && (
          <Box sx={{ px: SP.card, pt: SP.card }}>
            <Alert severity="warning">{kappungsText}</Alert>
          </Box>
        )}

        {/* 🔴 Skeleton, kein Spinner beim Seitenaufbau (Anti-Pattern 11) —
            und NUR beim ERSTEN Aufbau: ein Nachladen laesst die Zeilen stehen
            und springt nicht auf 0 (§2.2a Nr. 5). */}
        {laedt && !jeGeladen && (
          <Box data-testid="liste-skelett" sx={{ px: SP.card, py: SP.card }}>
            <Stack spacing={SP.tight}>
              {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
            </Stack>
          </Box>
        )}

        {!istSchmal && !(laedt && !jeGeladen) && (
          <Box
            ref={tabelleRef}
            data-testid="liste-tabelle"
            aria-busy={laedt ? 'true' : 'false'}
            sx={{ px: SP.card, pt: SP.card }}
          >
            <TableViewport
              footerPadPx={0}
              priorities={SPALTEN}
              containerWidth={behaelterBreite}
              edgeHint
              sx={{ maxWidth: tabellenMax > 0 ? tabellenMax : undefined }}
            >
              <Table
                size="small"
                // 🔴 `fixed` + anteilige Breiten halten die Kopfzellen-x stabil
                // (P33); `minWidth` ist die Untergrenze, unter der der
                // `TableViewport` rollt statt zu quetschen.
                sx={{ tableLayout: 'fixed', minWidth: spaltenMinSumme }}
              >
                <TableHead>
                  <TableRow>
                    {sichtbarSpalte.has('tracking') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('tracking') }}>Tracking</TableCell>}
                    {sichtbarSpalte.has('stage') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('stage') }}>Stage</TableCell>}
                    {sichtbarSpalte.has('mode') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('mode') }}>Mode</TableCell>}
                    {sichtbarSpalte.has('route') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('route') }}>From → To</TableCell>}
                    {/* 🔴 Zwei Spalten, nie eine. */}
                    {sichtbarSpalte.has('hafen') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('hafen') }}>Port arrival</TableCell>}
                    {sichtbarSpalte.has('zustellung') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('zustellung') }}>Final delivery</TableCell>}
                    {sichtbarSpalte.has('stand') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('stand') }}>As of</TableCell>}
                    {sichtbarSpalte.has('hinweis') && <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('hinweis') }}>Notice</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sichtbareZeilen.map((s) => (
                    <TableRow
                      key={s.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/kn-shipments/${s.id}`)}
                    >
                      {sichtbarSpalte.has('tracking') && (
                        <TableCell sx={{ fontSize: FS.sm }}>
                          {/* 🔴 DER Tastaturweg zur Detailseite (WCAG 2.1.1).
                              Der Zeilenklick darunter ist eine Bequemlichkeit —
                              ein `<tr>` ist von sich aus KEIN Tabstopp. */}
                          <Link
                            component={RouterLink}
                            to={`/kn-shipments/${s.id}`}
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                          >
                            {s.tracking_number}
                          </Link>
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('stage') && (
                        <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                          {stufeText(s.current_stage)}
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('mode') && (
                        <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                          {s.mode_of_transport || STRICH}
                          {s.service_type ? ` · ${s.service_type}` : ''}
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('route') && (
                        <TableCell sx={{ fontSize: FS.sm }}>{routeText(s)}</TableCell>
                      )}
                      {sichtbarSpalte.has('hafen') && (
                        <TableCell>
                          <TerminZelle
                            zeitpunkt={s.hafenankunft?.zeitpunkt}
                            art={s.hafenankunft?.art}
                          />
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('zustellung') && (
                        <TableCell>
                          <TerminZelle {...zustellung(s)} />
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('stand') && (
                        <TableCell sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                          {formatBerlinDateTime(s.fetched_at)}
                        </TableCell>
                      )}
                      {sichtbarSpalte.has('hinweis') && (
                        <TableCell>
                          <LeistungsHinweis bild={s.leistungsbild} knapp />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* 🔴 Der Tabellenkopf BLEIBT im Leerzustand stehen, und der
                      Leerzustand steht IN der Tabelle: P33 vergleicht die
                      Kopfzellen-x zwischen `grund` und `?q=zzzz`. */}
                  {leerGrund && (
                    <TableRow>
                      <TableCell colSpan={sichtbareSpalten.length || 1} sx={{ borderBottom: 0 }}>
                        <Leerzustand
                          grund={leerGrund}
                          q={q}
                          fehler={fehler}
                          onRetry={wiederholen}
                          onClearSearch={clearAll}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableViewport>
          </Box>
        )}

        {istSchmal && !(laedt && !jeGeladen) && (
          <Box data-testid="liste-karte" sx={{ px: SP.card, pt: SP.card }}>
            {leerGrund ? (
              <Leerzustand
                grund={leerGrund}
                q={q}
                fehler={fehler}
                onRetry={wiederholen}
                onClearSearch={clearAll}
              />
            ) : (
              <Stack spacing={SP.tight}>
                {sichtbareZeilen.map((s) => (
                  <MobileRecordCard
                    key={s.id}
                    // Die Marke des Kartenkastens — P15 laeuft ueber ALLE Karten
                    // der Zone, nicht nur ueber die erste.
                    data-sendung-karte=""
                    onClick={() => navigate(`/kn-shipments/${s.id}`)}
                    // 🔴 Der Titel ist der WEG HINEIN (der Weg, den ein Finger
                    // trifft). `TOUCH_TARGET_COARSE` als FLÄCHE um den Kasten,
                    // nicht als Kasten: sie waechst nach aussen, ohne das
                    // Layout zu ruecken (Hausform `RMAFaelligkeit.jsx:487-511`).
                    title={(
                      <Link
                        component={RouterLink}
                        to={`/kn-shipments/${s.id}`}
                        onClick={(e) => e.stopPropagation()}
                        underline="hover"
                        variant="body2"
                        // 🔴 R15 W4 (Nachbesserung 1, 2026-10-22): der 44-px-Boden
                        // kommt aus `MobileRecordCard.KARTE_TITEL_BASIS_SX`, nicht
                        // als Kopie. Der Baustein exportiert genau diesen Block
                        // seit demselben Tag mit dem Satz „Aendert eine Seite ihn,
                        // driften die anderen still." — drei Seiten trugen ihn
                        // vorher von Hand.
                        sx={{ ...KARTE_TITEL_BASIS_SX, color: 'inherit' }}
                      >
                        {s.tracking_number}
                      </Link>
                    )}
                    // 🔴 Die K&N-Kennung. `MobileRecordCard` rendert den
                    // `id`-Slot bereits monospace und `nowrap` OHNE
                    // `text-overflow` — die Zusage „nie gekuerzt" (N11.5) ist
                    // dort gebaut, nicht hier nachgebaut.
                    id={s.source_shipment_id ?? STRICH}
                    meta={(
                      <Stack spacing={SP.chip}>
                        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                          {s.mode_of_transport || STRICH}
                          {s.service_type ? ` · ${s.service_type}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: FS.xs }}>
                          {routeText(s)}
                        </Typography>
                        <Stack direction="row" gap={SP.card} flexWrap="wrap">
                          <Stack>
                            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>Port arrival</Typography>
                            <TerminZelle
                              zeitpunkt={s.hafenankunft?.zeitpunkt}
                              art={s.hafenankunft?.art}
                            />
                          </Stack>
                          <Stack>
                            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>Final delivery</Typography>
                            <TerminZelle {...zustellung(s)} />
                          </Stack>
                        </Stack>
                        {/* 🔴 Der Leistungshinweis ist eine ANGABE, kein Status:
                            er kann fehlen und darf P22 nicht tragen. Deshalb
                            steht er hier in `meta`, nicht in `chips`. */}
                        <LeistungsHinweis bild={s.leistungsbild} knapp />
                      </Stack>
                    )}
                    // 🔴 `chips` traegt die STAGE — den einzigen Wert, den P22
                    // mobil pruefen kann („Status nie ueber Farbe allein").
                    chips={<Pill>{stufeText(s.current_stage)}</Pill>}
                    fieldTestIds={{
                      title: `karte-titel-${s.id}`,
                      id: `karte-kennung-${s.id}`,
                      meta: `karte-meta-${s.id}`,
                      chips: `karte-status-${s.id}`,
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* 🔴 Die Fusszeile steht NUR mit Inhalt (N8.7) — ohne die Waechterung
            stuende im `no-match` ein leerer Fusszeilen-Behaelter, und `messeP33`
            meldete ihn.
            🔴 **R16 W1 (Nachbesserung 1, 2026-10-22): „mit Inhalt" heisst
            `> ROWS_PER_PAGE`, nicht `> 0`.** `TableFooterSimple` zeigt den
            Blaetterer erst ab `count > rowsPerPage`; bei 3 Zeilen hatte der
            Kasten kein Kind, behielt aber `minHeight: 44`, `borderTop` und
            `position: sticky` — ein leerer, deckender 44-px-Balken (am Foto
            66 px Leerflaeche unter der letzten Zeile, davon 44 die Leiste).
            Die Leiste erscheint jetzt genau dann, wenn sie blaettert. */}
        {sendungen.length > ROWS_PER_PAGE && (
          <Box data-testid="liste-fuss">
            <TableFooterSimple
              count={sendungen.length}
              page={seite - 1}
              onPageChange={(p) => set('page', p + 1)}
              rowsPerPage={ROWS_PER_PAGE}
              // 🔴 BS17 (das EINE Opt-in dieser Karte): mobil steht die Leiste im
              // Fluss nach der letzten Karte, statt ueber der schwebenden
              // Unterleiste zu kleben. Kein `leftSlot` — der gehoert den Summen.
              stickyOnMobile={false}
            />
          </Box>
        )}

        {sendungen.length > 0 && (
          <Box sx={{ px: SP.card, py: SP.card }}>
            <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
              {/* 🔴 KEIN Satz ueber die Buchung. Ob eine Zustellung beauftragt
                  ist, sagt der Leistungshinweis — und der sagt es BEDINGT, weil
                  die Mehrzahl der Codes unbekannt ist. Eine Legende gilt fuer
                  die ganze Liste und kann diese Bedingung nicht tragen. */}
              {STRICH} means: Kühne+Nagel supplied nothing for this.
            </Typography>
          </Box>
        )}
      </Panel>
    </PageContainer>
  )
}

/**
 * Die drei Leerzustaende an EINER Stelle — Tabelle und Karte teilen sie.
 *
 * 🔴 **`no-data` und `no-match` sind verschiedene Objekte**, nicht zwei Texte
 * desselben: der eine sagt „es gibt nichts", der andere „dein Begriff trifft
 * nichts" — und nur der zweite hat einen Weg heraus („Clear search").
 * `error` traegt „Try again", weil ein gescheiterter Abruf wiederholbar ist.
 */
function Leerzustand({ grund, q, fehler, onRetry, onClearSearch }) {
  if (grund === 'error') {
    return (
      <EmptyState
        reason="error"
        // 🔴 Der Satz nennt den Grund UND den Weg heraus („Try again"). Der
        //    Wortlaut ist der bisherige — die Jest-Probe haengt daran.
        message={`Shipments could not be loaded: ${fehler}`}
        onRetry={onRetry}
        // 🔴 R15 B3, Nachsatz (Nachbesserung 1, 2026-10-22): die Sperrmeldung
        //    darueber sagt „Use Try again below." — und `EmptyState` schweigt im
        //    BESTAETIGTEN Ausfall (`:152`), solange `showDuringOutage` fehlt. Der
        //    Verweis zeigte dann ins Leere. Diese Flaeche traegt ihre Aussage
        //    selbst (der globale Banner liegt unter jedem Modal), also ist sie
        //    genau der in `EmptyState` dokumentierte Opt-in-Fall.
        showDuringOutage
      />
    )
  }
  if (grund === 'no-match') {
    return (
      <EmptyState
        reason="no-match"
        query={q}
        noun="shipment"
        onClearSearch={onClearSearch}
      />
    )
  }
  return (
    <EmptyState
      reason="no-data"
      // 🔴 Der Wortlaut bleibt der bisherige — die Jest-Probe haengt daran.
      message="No shipments mirrored yet."
      // 🔴 BEIDE Lesarten. „Der erste Lauf kommt noch" allein waere falsch,
      // sobald er einmal gelaufen ist und nichts fand.
      detail={
        'This list shows what the nightly pull last found at Kühne+Nagel. '
        + 'It runs at 02:00 UTC as a post-step of the daily sync. If nothing '
        + 'shows here, either it has not run yet, or Kühne+Nagel currently '
        + 'holds no shipment for us.'
      }
    />
  )
}
